import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { roleLabel, dateFmt, formatErr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
    AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
    AlertDialogFooter, AlertDialogCancel, AlertDialogAction, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { MfaSecurityCard } from "@/components/MfaSecurityCard";
import { toast } from "sonner";
import { Loader2, Mail, Send, Trash2, Save, ShieldCheck, ShieldAlert, Lock, RotateCcw, Users } from "lucide-react";

/* ---------------- SMTP2GO email ---------------- */
function Smtp2goCard() {
    const [cfg, setCfg] = useState(null);
    const [form, setForm] = useState({ sender: "", from_name: "ProcureEd", api_key: "" });
    const [testTo, setTestTo] = useState("");
    const [busy, setBusy] = useState("");

    const load = () => api.get("/admin/integrations/smtp2go").then((r) => {
        setCfg(r.data);
        setForm((f) => ({ ...f, sender: r.data.sender || "", from_name: r.data.from_name || "ProcureEd" }));
    }).catch(() => setCfg({ configured: false }));
    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!form.sender || !form.api_key) { toast.error("Verified sender and API key are required"); return; }
        setBusy("save");
        try { await api.put("/admin/integrations/smtp2go", form); toast.success("SMTP2GO settings saved"); setForm((f) => ({ ...f, api_key: "" })); load(); }
        catch (err) { toast.error(formatErr(err.response?.data?.detail)); } finally { setBusy(""); }
    };
    const test = async () => {
        if (!testTo) { toast.error("Enter an address to send the test to"); return; }
        setBusy("test");
        try { await api.post("/admin/integrations/smtp2go/test", { to: testTo }); toast.success(`Test email sent to ${testTo}`); }
        catch (err) { toast.error(formatErr(err.response?.data?.detail)); } finally { setBusy(""); }
    };
    const remove = async () => {
        try { await api.delete("/admin/integrations/smtp2go"); toast.success("Email settings removed"); setForm({ sender: "", from_name: "ProcureEd", api_key: "" }); load(); }
        catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    };

    return (
        <div className="bg-white border border-border rounded-xl overflow-hidden" data-testid="smtp2go-card">
            <div className="px-5 py-4 border-b border-border flex items-center gap-2 text-brand-emerald font-semibold">
                <Mail className="h-4 w-4 text-brand-teal" /> Email (SMTP2GO)
                {cfg?.configured && <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-800 border border-emerald-200" data-testid="smtp2go-configured-badge">Configured</span>}
            </div>
            <div className="p-5 space-y-4">
                <p className="text-sm text-muted-foreground">Send security alerts, welcome emails and approval/PO notifications. Your API key is encrypted at rest and never shown again.</p>
                <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label className="text-xs">Verified sender email</Label><Input value={form.sender} onChange={(e) => setForm({ ...form, sender: e.target.value })} placeholder="no-reply@procureed.app" className="mt-1" data-testid="smtp2go-sender" /></div>
                    <div><Label className="text-xs">From name</Label><Input value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })} placeholder="ProcureEd" className="mt-1" data-testid="smtp2go-from-name" /></div>
                    <div className="sm:col-span-2"><Label className="text-xs">SMTP2GO API key {cfg?.configured && <span className="text-muted-foreground">(saved: {cfg.api_key_hint} — leave blank to keep)</span>}</Label>
                        <Input type="password" autoComplete="off" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} placeholder="api-••••••••••••••••" className="mt-1 font-mono" data-testid="smtp2go-api-key" /></div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Button disabled={busy} onClick={save} className="bg-brand-emerald hover:bg-brand-emeraldhover rounded-lg" data-testid="smtp2go-save">
                        {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save</>}
                    </Button>
                    {cfg?.configured && (
                        <AlertDialog>
                            <AlertDialogTrigger asChild><Button variant="ghost" className="text-rose-600" data-testid="smtp2go-remove"><Trash2 className="h-4 w-4 mr-1" /> Remove</Button></AlertDialogTrigger>
                            <AlertDialogContent>
                                <AlertDialogHeader><AlertDialogTitle>Remove email settings?</AlertDialogTitle>
                                    <AlertDialogDescription>ProcureEd will stop sending emails until SMTP2GO is configured again.</AlertDialogDescription></AlertDialogHeader>
                                <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={remove} data-testid="smtp2go-remove-confirm">Remove</AlertDialogAction></AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    )}
                </div>
                <div className="border-t border-border pt-4 flex flex-wrap items-end gap-2">
                    <div className="flex-1 min-w-[200px]"><Label className="text-xs">Send a test email to</Label><Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@school.edu" className="mt-1" data-testid="smtp2go-test-to" /></div>
                    <Button disabled={busy || !cfg?.configured} variant="outline" onClick={test} className="rounded-lg" data-testid="smtp2go-test">
                        {busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Send test</>}
                    </Button>
                </div>
                {!cfg?.configured && <p className="text-[11px] text-muted-foreground">Save your SMTP2GO settings before sending a test.</p>}
            </div>
        </div>
    );
}

/* ---------------- Admin 2-step management ---------------- */
function AdminMfaTable() {
    const [rows, setRows] = useState(null);
    const load = () => api.get("/admin/users").then((r) => setRows(r.data)).catch(() => setRows([]));
    useEffect(() => { load(); }, []);

    const reset = async (u) => {
        try { await api.post(`/admin/users/${u.id}/mfa/reset`); toast.success(`${u.name}'s authenticator was reset`); load(); }
        catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    };
    const setRequired = async (u, required) => {
        try { await api.post(`/admin/users/${u.id}/mfa/require`, { required }); toast.success(required ? "2-step required" : "2-step requirement disabled"); load(); }
        catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    };

    return (
        <div className="bg-white border border-border rounded-xl overflow-hidden" data-testid="admin-mfa-table-card">
            <div className="px-5 py-4 border-b border-border flex items-center gap-2 text-brand-emerald font-semibold"><Users className="h-4 w-4 text-brand-teal" /> Admin 2-step verification</div>
            {rows === null ? (
                <div className="grid place-items-center py-12 text-brand-emerald"><Loader2 className="h-6 w-6 animate-spin" /></div>
            ) : (
                <div className="overflow-x-auto pe-scroll">
                    <table className="w-full text-sm" data-testid="admin-mfa-table">
                        <thead><tr className="text-left text-muted-foreground border-b border-border bg-secondary/40">
                            <th className="p-3 font-medium">Admin</th><th className="p-3 font-medium">School</th>
                            <th className="p-3 font-medium">Roles</th><th className="p-3 font-medium">2-step</th>
                            <th className="p-3 font-medium">Required</th><th className="p-3 font-medium text-right">Actions</th>
                        </tr></thead>
                        <tbody>
                            {rows.map((u) => (
                                <tr key={u.id} className="border-b border-border last:border-0 hover:bg-secondary/40" data-testid={`admin-mfa-row-${u.id}`}>
                                    <td className="p-3"><div className="font-medium">{u.name}</div><div className="text-xs text-muted-foreground">{u.email}</div></td>
                                    <td className="p-3">{u.tenant_name}</td>
                                    <td className="p-3"><div className="flex flex-wrap gap-1">{(u.roles || []).map((r) => <span key={r} className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">{roleLabel(r)}</span>)}</div></td>
                                    <td className="p-3">
                                        <div className="flex items-center gap-1.5">
                                            {u.mfa_enabled ? (
                                                <span className="inline-flex items-center gap-1 text-emerald-700" data-testid={`mfa-state-${u.id}`}><ShieldCheck className="h-3.5 w-3.5" /> Enrolled</span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-amber-700" data-testid={`mfa-state-${u.id}`}><ShieldAlert className="h-3.5 w-3.5" /> Not set up</span>
                                            )}
                                            {u.mfa_locked && <span className="inline-flex items-center gap-1 text-rose-700 text-xs" data-testid={`mfa-locked-${u.id}`}><Lock className="h-3 w-3" /> Locked</span>}
                                        </div>
                                    </td>
                                    <td className="p-3"><Switch checked={u.mfa_required} onCheckedChange={(v) => setRequired(u, v)} data-testid={`mfa-require-toggle-${u.id}`} /></td>
                                    <td className="p-3 text-right">
                                        <AlertDialog>
                                            <AlertDialogTrigger asChild>
                                                <Button variant="outline" size="sm" disabled={!u.mfa_enabled && !u.mfa_locked} data-testid={`reset-mfa-${u.id}`}><RotateCcw className="h-4 w-4 mr-1" /> Reset</Button>
                                            </AlertDialogTrigger>
                                            <AlertDialogContent>
                                                <AlertDialogHeader><AlertDialogTitle>Reset 2-step for {u.name}?</AlertDialogTitle>
                                                    <AlertDialogDescription>Their authenticator app and recovery codes will stop working. They'll set up a new authenticator on their next sign-in. They'll be emailed if email is configured.</AlertDialogDescription></AlertDialogHeader>
                                                <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
                                                    <AlertDialogAction className="bg-brand-emerald hover:bg-brand-emeraldhover" onClick={() => reset(u)} data-testid={`reset-mfa-confirm-${u.id}`}>Reset authenticator</AlertDialogAction></AlertDialogFooter>
                                            </AlertDialogContent>
                                        </AlertDialog>
                                    </td>
                                </tr>
                            ))}
                            {rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No admin accounts yet.</td></tr>}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export function AdminSecurity() {
    return (
        <div className="space-y-6" data-testid="admin-security">
            <div className="grid lg:grid-cols-2 gap-6">
                <Smtp2goCard />
                <MfaSecurityCard />
            </div>
            <AdminMfaTable />
        </div>
    );
}
